"use client"

import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"

import { cn } from "../../lib/utils"

type LinearProgressProps = {
  value: number
  color?: string
  labelPosition?: "right" | "bottom-right"
  className?: string
  labelClassName?: string
} & React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>

const LinearProgress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  LinearProgressProps
>(
  (
    {
      className,
      labelClassName,
      value,
      color,
      labelPosition = "right",
      ...props
    },
    ref
  ) => {
    const progress = Math.max(0, Math.min(100, value))

    return (
      <div
        className={cn(
          "flex",
          labelPosition === "bottom-right"
            ? "flex-col items-end gap-1"
            : "items-center gap-2",
          className
        )}
      >
        <ProgressPrimitive.Root
          ref={ref}
          className={cn(
            "border-1 relative h-2 w-full overflow-hidden rounded-full bg-gradient-to-b from-[#E3E8EF] to-[#EEF2F6] " +
              "",
            labelPosition === "bottom-right" ? "w-full" : "flex-1"
          )}
          {...props}
        >
          <ProgressPrimitive.Indicator
            className={cn(
              "size-full flex-1 shadow-[0_1.2px_2.4px_0_rgba(0,0,0,0.06),_0_1.2px_3.6px_0_rgba(0,0,0,0.10)] transition-all ",
              color ?? "bg-primary-700"
            )}
            style={{
              transform: `translateX(-${100 - (value || 0)}%)`,
            }}
          />
        </ProgressPrimitive.Root>
      </div>
    )
  }
)
LinearProgress.displayName = "LinearProgress"

type CircularProgressProps = {
  value: number
  size?: number
  color?: string
  className?: string
  labelClassName?: string
} & React.HTMLAttributes<HTMLDivElement>

const CircularProgress = React.forwardRef<
  HTMLDivElement,
  CircularProgressProps
>(({ className, labelClassName, value, size = 120, color, ...props }, ref) => {
  const progress = Math.max(0, Math.min(100, value))

  const radius = size / 2 - 5
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (progress / 100) * circumference
  return (
    <div
      ref={ref}
      className={cn(
        `relative flex items-center justify-center w-[${size}] h-[${size}]`,
        className
      )}
      {...props}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E3E8EF"
          strokeWidth="10"
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color ?? "#0B5CF2"}
          strokeWidth="10"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(180 ${size / 2} ${size / 2})`}
          className="transition-all"
        />
      </svg>
      <span
        className={cn(
          "absolute text-center text-sm font-medium leading-normal text-white",
          labelClassName
        )}
      >
        {progress}%
      </span>
    </div>
  )
})
CircularProgress.displayName = "CircularProgress"

type HalfCircularProgressProps = {
  value: number
  size?: number
  color?: string
  className?: string
  labelClassName?: string
} & React.HTMLAttributes<HTMLDivElement>

const HalfCircularProgress = React.forwardRef<
  HTMLDivElement,
  HalfCircularProgressProps
>(({ className, labelClassName, value, size = 120, color, ...props }, ref) => {
  const progress = Math.max(0, Math.min(100, value))

  const radius = size / 2 - 10
  const circumference = Math.PI * radius
  const strokeDashoffset = circumference * (1 - progress / 100)
  const width = size
  const height = size / 2 + 10
  return (
    <div
      ref={ref}
      className={cn("relative flex items-center justify-center", className)}
      style={{ width, height }}
      {...props}
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <path
          d={`M 10,${height - 10} A ${radius},${radius} 0 0,1 ${width - 10},${height - 10}`}
          stroke="#E3E8EF"
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d={`M 10,${height - 10} A ${radius},${radius} 0 0,1 ${width - 10},${height - 10}`}
          stroke={color ?? "#0B5CF2"}
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all"
        />
      </svg>
      <span
        className={cn(
          "absolute bottom-1 text-center text-sm font-medium leading-normal text-white",
          labelClassName
        )}
      >
        {progress}%
      </span>
    </div>
  )
})
HalfCircularProgress.displayName = "HalfCircularProgress"

type ProgressProps = {
  value: number
  variant?: "linear" | "circular" | "half-circular"
  size?: number
  color?: string
  labelPosition?: "right" | "bottom-right"
  className?: string
  labelClassName?: string
} & React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(
  (
    {
      className,
      labelClassName,
      value,
      variant = "linear",
      size = 120,
      color,
      labelPosition = "right",
      ...props
    },
    ref
  ) => {
    if (variant === "linear") {
      return (
        <LinearProgress
          ref={ref}
          value={value}
          color={color}
          className={className}
          labelClassName={labelClassName}
          labelPosition={labelPosition}
          {...props}
        />
      )
    }
    if (variant === "circular") {
      return (
        <CircularProgress
          value={value}
          size={size}
          color={color}
          className={className}
          labelClassName={labelClassName}
          {...props}
        />
      )
    }
    if (variant === "half-circular") {
      return (
        <HalfCircularProgress
          value={value}
          size={size}
          color={color}
          className={className}
          labelClassName={labelClassName}
          {...props}
        />
      )
    }
    return null
  }
)
Progress.displayName = "Progress"

export { Progress, LinearProgress, CircularProgress, HalfCircularProgress }
